import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../components/ui/dialog';
import {
  Search, MapPin, Users, Clock, Loader2, X,
  FlaskConical, Code, Cog, Calculator, Bot, BookOpen, Sparkles, Filter, School, ChevronDown,
} from 'lucide-react';
import axios from 'axios';
import { formatTime } from '../../utils/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  science:     { label: 'Science',     color: 'from-rose-500 to-pink-400',    bg: 'bg-rose-500',    icon: FlaskConical },
  technology:  { label: 'Technology',  color: 'from-blue-500 to-indigo-400',  bg: 'bg-blue-500',    icon: Code },
  engineering: { label: 'Engineering', color: 'from-emerald-500 to-teal-400', bg: 'bg-emerald-500', icon: Cog },
  math:        { label: 'Math',        color: 'from-purple-500 to-violet-400', bg: 'bg-purple-500',  icon: Calculator },
  robotics:    { label: 'Robotics',    color: 'from-orange-500 to-amber-400', bg: 'bg-orange-500',  icon: Bot },
};

const CATEGORY_INFO = {
  science: {
    tagline: 'From Lab Coats to White Coats',
    description: 'Hands-on science kits, real lab techniques, and themed adventures that build the foundation for medicine, research, and every science career. Designed for grades 3–8.',
    href: '/science',
  },
  technology: {
    tagline: 'The Founding Division of Ready Set STEM',
    description: 'From Scratch for 5-year-olds to AI and cybersecurity for teens — our STEM.org Best In STEM™ (Top 5%) technology program builds the digital skills that define the future.',
    href: '/technology',
  },
  engineering: {
    tagline: 'Design. Build. Engineer the Future.',
    description: 'CAD design, 3D printing, mechanical engineering, bridge building, and architecture — our engineering division turns students into makers and problem-solvers. Every student gets their 3D prints shipped home.',
    href: '/engineering',
  },
  math: {
    tagline: 'Competition Math for Future Champions.',
    description: 'From math games for kindergartners to AMC and MATHCOUNTS prep for middle schoolers — programs that build critical thinking, problem-solving skills, and mathematical reasoning for grades K–8.',
    href: '/math',
  },
  robotics: {
    tagline: 'Build the Future. One Robot at a Time.',
    description: 'From first LEGO builds at age 6 to FANUC-certified careers — the most comprehensive robotics pipeline in the greater Chicago region.',
    href: '/robotics',
  },
};

const normalizeCategory = (cat) => {
  const lower = cat?.toLowerCase() || '';
  return lower === 'coding' ? 'technology' : lower;
};

const GRADE_ORDER = ['K-2', '3-5', '6-8', '9-12'];

const GRADE_COLORS = {
  'K-2':  'bg-emerald-100 text-emerald-700',
  '3-5':  'bg-blue-100 text-blue-700',
  '6-8':  'bg-purple-100 text-purple-700',
  '9-12': 'bg-orange-100 text-orange-700',
};

const sectionDurationMin = (s) => {
  if (!s.start_time || !s.end_time) return null;
  const [sh, sm] = s.start_time.split(':').map(Number);
  const [eh, em] = s.end_time.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  return diff > 0 ? diff : null;
};

const formatDuration = (min) => {
  if (!min) return '';
  if (min < 60) return `${min} min`;
  if (min % 60 === 0) return `${min / 60} hr${min / 60 !== 1 ? 's' : ''}`;
  return `${Math.floor(min / 60)}h ${min % 60}m`;
};

const effectiveBands = (s, course) =>
  s.grade_bands?.length > 0 ? s.grade_bands : (course?.grade_bands || []);

const locationKey = (s) => String(s.location_id || s.location || '');

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses]     = useState([]);
  const [sections, setSections]   = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filters, setFilters]     = useState({
    search:      searchParams.get('search')   || '',
    gradeBand:   searchParams.get('grade')    || '',
    category:    searchParams.get('category') || '',
    locationId:  searchParams.get('location') || '',
    startWindow: searchParams.get('start')    || '',
  });

  const [expandedCourseId, setExpandedCourseId] = useState(null);
  const [picker, setPicker] = useState({ locationId: '', gradeBand: '', duration: '' });
  const [showAll, setShowAll] = useState(searchParams.get('show_all') === 'true');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [coursesRes, sectionsRes, locationsRes] = await Promise.all([
          axios.get(`${API_URL}/api/courses/`,   { params: { page_size: 5000 } }),
          axios.get(`${API_URL}/api/sections/`,  { params: { page_size: 5000 } }),
          axios.get(`${API_URL}/api/locations/`, { params: { page_size: 200 } }),
        ]);
        setCourses(coursesRes.data?.results   || coursesRes.data   || []);
        setSections(sectionsRes.data?.results || sectionsRes.data  || []);
        setLocations(locationsRes.data?.results || locationsRes.data || []);
      } catch (err) {
        console.error('Failed to fetch catalog data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Sync filters → URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.category)    params.set('category', filters.category);
    if (filters.search)      params.set('search',   filters.search);
    if (filters.gradeBand)   params.set('grade',    filters.gradeBand);
    if (filters.locationId)  params.set('location', filters.locationId);
    if (filters.startWindow) params.set('start',    filters.startWindow);
    if (showAll)             params.set('show_all', 'true');
    setSearchParams(params, { replace: true });
  }, [filters, showAll, setSearchParams]);

  const clearFilters = () => { setFilters({ search: '', gradeBand: '', category: '', locationId: '', startWindow: '' }); setShowAll(false); };
  const hasActiveFilters = Object.values(filters).some(v => v) || showAll;

  const courseById = useMemo(() => new Map(courses.map(c => [String(c.id), c])), [courses]);
  const todayIso   = new Date().toISOString().slice(0, 10);

  const startWindowMax = useMemo(() => {
    if (!filters.startWindow) return null;
    const d = new Date();
    if (filters.startWindow === 'week')  d.setDate(d.getDate() + 7);
    else if (filters.startWindow === 'month') d.setDate(d.getDate() + 30);
    else if (filters.startWindow === '60')    d.setDate(d.getDate() + 60);
    else return null;
    return d.toISOString().slice(0, 10);
  }, [filters.startWindow]);

  // Group active, upcoming, non-private class sections by course
  const sectionsByCourse = useMemo(() => {
    const map = {};
    sections.forEach(s => {
      if (!s.active || s.is_private) return;
      const endGate = s.end_date || s.start_date;
      if (endGate && endGate < todayIso) return;
      if (s.start_date && s.start_date < todayIso) return;
      const course = courseById.get(String(s.course_id || s.course));
      if (!course || course.active === false) return;
      if (/^W\d+D\d+:/.test(course.title)) return;
      const cid = String(s.course_id || s.course);
      if (!map[cid]) map[cid] = [];
      map[cid].push(s);
    });
    return map;
  }, [sections, courseById, todayIso]);

  // Check if a section matches the current top-bar filters (excluding category/search which are course-level)
  const sectionMatchesFilters = (s, course) => {
    if (filters.gradeBand && !effectiveBands(s, course).includes(filters.gradeBand)) return false;
    if (filters.locationId && locationKey(s) !== String(filters.locationId)) return false;
    if (startWindowMax && s.start_date && s.start_date > startWindowMax) return false;
    return true;
  };

  // Filter courses: show only those with at least one section matching all active filters.
  // When showAll is on, also include courses with no sessions (they bypass location/grade/date filters).
  const filteredCourses = useMemo(() => {
    return courses
      .filter(course => {
        if (course.active === false) return false;
        if (/^W\d+D\d+:/.test(course.title)) return false;
        const courseSections = sectionsByCourse[String(course.id)] || [];
        const hasSessions = courseSections.length > 0;
        if (!hasSessions && !showAll) return false;
        if (filters.category && normalizeCategory(course.category) !== filters.category) return false;
        if (filters.search) {
          const q = filters.search.toLowerCase();
          if (!`${course.title} ${course.description}`.toLowerCase().includes(q)) return false;
        }
        if (!hasSessions) {
          if (filters.gradeBand && !(course.grade_bands || []).includes(filters.gradeBand)) return false;
          return true;
        }
        return courseSections.some(s => sectionMatchesFilters(s, course));
      })
      .sort((a, b) => {
        const aDate = (sectionsByCourse[String(a.id)] || []).map(s => s.start_date).filter(Boolean).sort()[0] || '9999';
        const bDate = (sectionsByCourse[String(b.id)] || []).map(s => s.start_date).filter(Boolean).sort()[0] || '9999';
        return aDate.localeCompare(bDate);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, sectionsByCourse, filters, startWindowMax, showAll]);

  // Category counts — same filter minus category itself
  const categoryCounts = useMemo(() => {
    const counts = {};
    courses
      .filter(course => {
        if (course.active === false) return false;
        if (/^W\d+D\d+:/.test(course.title)) return false;
        const cs = sectionsByCourse[String(course.id)] || [];
        const hasSessions = cs.length > 0;
        if (!hasSessions && !showAll) return false;
        if (filters.search) {
          const q = filters.search.toLowerCase();
          if (!`${course.title} ${course.description}`.toLowerCase().includes(q)) return false;
        }
        if (!hasSessions) return true;
        return cs.some(s => sectionMatchesFilters(s, course));
      })
      .forEach(c => {
        const key = normalizeCategory(c.category);
        if (key) counts[key] = (counts[key] || 0) + 1;
      });
    return counts;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, sectionsByCourse, filters, startWindowMax, showAll]);

  // ── Picker helpers ─────────────────────────────────────────────────────

  const expandCourse = (courseId) => {
    if (expandedCourseId === courseId) {
      setExpandedCourseId(null);
      return;
    }
    setExpandedCourseId(courseId);

    const course       = courseById.get(courseId);
    const courseSects  = sectionsByCourse[courseId] || [];

    // Pre-populate location from top-bar filter if valid for this course
    const validLocId = filters.locationId &&
      courseSects.some(s => locationKey(s) === String(filters.locationId))
      ? filters.locationId : '';

    const afterLoc = validLocId
      ? courseSects.filter(s => locationKey(s) === validLocId)
      : courseSects;

    // Pre-populate grade band from filter, or auto-select if only one option
    const bandSet = new Set();
    afterLoc.forEach(s => effectiveBands(s, course).forEach(b => bandSet.add(b)));
    const availBands = GRADE_ORDER.filter(b => bandSet.has(b));
    const validGrade =
      (filters.gradeBand && availBands.includes(filters.gradeBand)) ? filters.gradeBand :
      availBands.length === 1 ? availBands[0] : '';

    // Auto-select duration if only one option
    let validDuration = '';
    if (validGrade) {
      const afterBand = afterLoc.filter(s => effectiveBands(s, course).includes(validGrade));
      const durSet = new Set();
      afterBand.forEach(s => { const d = sectionDurationMin(s); if (d) durSet.add(d); });
      if (durSet.size === 1) validDuration = String([...durSet][0]);
    }

    setPicker({ locationId: validLocId, gradeBand: validGrade, duration: validDuration });
  };

  const handlePickerLocation = (locationId) => {
    const course      = courseById.get(expandedCourseId);
    const courseSects = sectionsByCourse[expandedCourseId] || [];
    const afterLoc    = courseSects.filter(s => locationKey(s) === locationId);

    const bandSet = new Set();
    afterLoc.forEach(s => effectiveBands(s, course).forEach(b => bandSet.add(b)));
    const availBands = GRADE_ORDER.filter(b => bandSet.has(b));
    const autoGrade  = availBands.length === 1 ? availBands[0] : '';

    let autoDuration = '';
    if (autoGrade) {
      const afterBand = afterLoc.filter(s => effectiveBands(s, course).includes(autoGrade));
      const durSet = new Set();
      afterBand.forEach(s => { const d = sectionDurationMin(s); if (d) durSet.add(d); });
      if (durSet.size === 1) autoDuration = String([...durSet][0]);
    }

    setPicker({ locationId, gradeBand: autoGrade, duration: autoDuration });
  };

  const handlePickerGradeBand = (gradeBand) => {
    const course      = courseById.get(expandedCourseId);
    const courseSects = sectionsByCourse[expandedCourseId] || [];
    const afterLoc    = picker.locationId ? courseSects.filter(s => locationKey(s) === picker.locationId) : courseSects;
    const afterBand   = afterLoc.filter(s => effectiveBands(s, course).includes(gradeBand));

    const durSet = new Set();
    afterBand.forEach(s => { const d = sectionDurationMin(s); if (d) durSet.add(d); });
    const autoDuration = durSet.size === 1 ? String([...durSet][0]) : '';

    setPicker(p => ({ ...p, gradeBand, duration: autoDuration }));
  };

  // Derived picker options for the currently expanded course
  const pickerData = useMemo(() => {
    if (!expandedCourseId) return null;
    const course      = courseById.get(expandedCourseId);
    if (!course) return null;
    const courseSects = (sectionsByCourse[expandedCourseId] || []).filter(s => !s.start_date || s.start_date >= todayIso);

    // Locations
    const locMap = {};
    courseSects.forEach(s => {
      const lid = locationKey(s);
      if (lid && !locMap[lid]) locMap[lid] = s.location_name || 'Online';
    });
    const availableLocations = Object.entries(locMap).map(([id, name]) => ({ id, name }));

    // After location selection
    const afterLoc = picker.locationId
      ? courseSects.filter(s => locationKey(s) === picker.locationId)
      : courseSects;

    // Grade bands
    const bandSet = new Set();
    afterLoc.forEach(s => effectiveBands(s, course).forEach(b => bandSet.add(b)));
    const availableGradeBands = GRADE_ORDER.filter(b => bandSet.has(b));

    // After grade band selection
    const afterBand = picker.gradeBand
      ? afterLoc.filter(s => effectiveBands(s, course).includes(picker.gradeBand))
      : afterLoc;

    // Durations
    const durSet = new Set();
    afterBand.forEach(s => { const d = sectionDurationMin(s); if (d) durSet.add(d); });
    const availableDurations = [...durSet].sort((a, b) => a - b);

    // Matched sections (all that satisfy location + grade + duration)
    let matchedSections = [];
    if (picker.locationId && picker.gradeBand && picker.duration) {
      matchedSections = afterBand.filter(s =>
        locationKey(s) === picker.locationId &&
        sectionDurationMin(s) === parseInt(picker.duration)
      );
    }

    return { availableLocations, availableGradeBands, availableDurations, matchedSections };
  }, [expandedCourseId, picker, sectionsByCourse, courseById, todayIso]);

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div data-testid="catalog-page">

      {/* ─── Filter bar ─────────────────────────────────────────────────── */}
      <section className="bg-white pt-8 pb-8" data-testid="catalog-filter-strip">
        <div className="container-app">
          <div className="bg-rsc-navy rounded-2xl shadow-xl px-4 sm:px-6 py-4 sm:py-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  className="pl-9 bg-white/10 border-white/15 text-white placeholder:text-white/40 h-11 rounded-full"
                  placeholder="Search courses…"
                  value={filters.search}
                  onChange={e => setFilters({ ...filters, search: e.target.value })}
                  data-testid="catalog-search"
                />
              </div>
              <Select value={filters.gradeBand || 'all'} onValueChange={v => setFilters({ ...filters, gradeBand: v === 'all' ? '' : v })}>
                <SelectTrigger className="w-[150px] bg-white/10 border-white/15 text-white h-11 rounded-full" data-testid="catalog-grade-select">
                  <Users className="w-3.5 h-3.5 mr-1.5 text-white/40" /><SelectValue placeholder="All Grades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Grades</SelectItem>
                  <SelectItem value="K-2">K–2</SelectItem>
                  <SelectItem value="3-5">Grades 3–5</SelectItem>
                  <SelectItem value="6-8">Grades 6–8</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.startWindow || 'any'} onValueChange={v => setFilters({ ...filters, startWindow: v === 'any' ? '' : v })}>
                <SelectTrigger className="w-[160px] bg-white/10 border-white/15 text-white h-11 rounded-full" data-testid="catalog-start-select">
                  <Clock className="w-3.5 h-3.5 mr-1.5 text-white/40" /><SelectValue placeholder="Any Start" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any Start</SelectItem>
                  <SelectItem value="week">Next 7 Days</SelectItem>
                  <SelectItem value="month">Next 30 Days</SelectItem>
                  <SelectItem value="60">Next 60 Days</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.locationId || 'all'} onValueChange={v => setFilters({ ...filters, locationId: v === 'all' ? '' : v })}>
                <SelectTrigger className="w-[200px] bg-white/10 border-white/15 text-white h-11 rounded-full" data-testid="catalog-location-select">
                  <MapPin className="w-3.5 h-3.5 mr-1.5 text-white/40" /><SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.filter(l => l.active).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {/* Available Now / All Courses toggle */}
              <div className="flex items-center rounded-full border border-white/20 overflow-hidden text-sm font-semibold shrink-0" data-testid="catalog-show-all-toggle">
                <button
                  onClick={() => setShowAll(false)}
                  className={`px-3 h-11 transition-all whitespace-nowrap ${!showAll ? 'bg-white text-rsc-navy' : 'text-white/60 hover:text-white'}`}
                  data-testid="catalog-toggle-available"
                >
                  Available Now
                </button>
                <button
                  onClick={() => setShowAll(true)}
                  className={`px-3 h-11 transition-all whitespace-nowrap ${showAll ? 'bg-white text-rsc-navy' : 'text-white/60 hover:text-white'}`}
                  data-testid="catalog-toggle-all"
                >
                  All Courses
                </button>
              </div>
              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters} className="text-white/60 hover:text-white h-11 rounded-full" data-testid="catalog-clear-filters">
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
              <div className="ml-auto text-sm text-white/60 whitespace-nowrap" data-testid="catalog-count">
                {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''} found
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Body ───────────────────────────────────────────────────────── */}
      <section className="py-8 bg-gray-50 min-h-[50vh]">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">

            {/* Sidebar */}
            <aside className="space-y-5" data-testid="catalog-sidebar">
              <div>
                <h2 className="text-[11px] font-bold tracking-wider uppercase text-gray-500 mb-3">
                  Browse by Category
                </h2>
                <div className="space-y-1.5">
                  <button
                    onClick={() => setFilters({ ...filters, category: '' })}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                      !filters.category
                        ? 'bg-rsc-yellow text-rsc-navy shadow-sm'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200/60'
                    }`}
                    data-testid="sidebar-cat-all"
                  >
                    <BookOpen className="w-4 h-4" />
                    <span className="flex-1 text-left">All Courses</span>
                    <span className="text-xs opacity-70">{Object.values(categoryCounts).reduce((a, b) => a + b, 0)}</span>
                  </button>
                  {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
                    const Icon  = cfg.icon;
                    const count = categoryCounts[key] || 0;
                    if (count === 0) return null;
                    const active = filters.category === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setFilters({ ...filters, category: key })}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                          active
                            ? 'bg-rsc-yellow text-rsc-navy shadow-sm'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200/60'
                        }`}
                        data-testid={`sidebar-cat-${key}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="flex-1 text-left">{cfg.label}</span>
                        <span className="text-xs opacity-70">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Division info card — shown when a category is selected */}
              {filters.category && CATEGORY_INFO[filters.category] && (() => {
                const info = CATEGORY_INFO[filters.category];
                const cfg  = CATEGORY_CONFIG[filters.category];
                const Icon = cfg.icon;
                return (
                  <div className={`rounded-xl overflow-hidden border border-gray-200/60`}>
                    <div className={`bg-gradient-to-br ${cfg.color} p-4`}>
                      <Icon className="w-5 h-5 text-white/80 mb-2" />
                      <p className="text-[11px] font-bold text-white/70 uppercase tracking-wide mb-0.5">{info.tagline}</p>
                      <h3 className="text-sm font-bold text-white leading-snug">{cfg.label} Division</h3>
                    </div>
                    <div className="bg-white p-4">
                      <p className="text-xs text-gray-500 leading-relaxed mb-3">{info.description}</p>
                      <Link to={info.href} className="text-xs font-semibold text-rsc-blue hover:underline">
                        Learn more about {cfg.label} →
                      </Link>
                    </div>
                  </div>
                );
              })()}

              {/* STEM.org callout */}
              <div className="rounded-xl bg-white border border-gray-200/60 p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <Sparkles className="w-4 h-4 text-rsc-yellow" />
                  <h3 className="text-sm font-bold text-rsc-navy">STEM.org Accredited</h3>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed mb-2">
                  See how our accredited programs give back to the communities we serve.
                </p>
                <Link to="/community-impact" className="text-xs font-semibold text-rsc-blue hover:underline">
                  See our impact →
                </Link>
              </div>

              {/* Summer 2027 interest card */}
              <div className="rounded-xl bg-white border border-gray-200/60 overflow-hidden">
                <div className="p-4 pb-3">
                  <h3 className="text-sm font-bold text-rsc-navy mb-1">Summer Camps 2027</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Summer 2026 is closed. Join the interest list to be first to know when 2027 registration opens.
                  </p>
                </div>
                <Link to="/summer-camps#browse-camps" className="block px-4 pb-4">
                  <Button className="w-full bg-rsc-navy text-white hover:bg-rsc-navy/90 font-bold rounded-full text-sm" data-testid="sidebar-summer-interest">
                    Get Notified
                  </Button>
                </Link>
                <img
                  src="https://res.cloudinary.com/dfa8raimq/image/upload/v1782163984/ChatGPT_Image_Jun_22_2026_11_20_39_AM_tmnjmn.jpg"
                  alt="Summer camp learner"
                  className="w-full h-32 object-cover"
                  loading="lazy"
                />
              </div>

              {/* Robotics hub card */}
              <div className="rounded-xl bg-white border border-orange-200/60 overflow-hidden">
                <div className="p-4 pb-3">
                  <h3 className="text-sm font-bold text-rsc-navy mb-1">Robotics Programs</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    FLL, FTC, VEX IQ &amp; FANUC industrial training — the full K–Career robotics pipeline.
                  </p>
                </div>
                <Link to="/robotics" className="block px-4 pb-4">
                  <Button className="w-full bg-orange-500 text-white hover:bg-orange-600 font-bold rounded-full text-sm" data-testid="sidebar-robotics-link">
                    Explore Robotics
                  </Button>
                </Link>
                <img
                  src="https://res.cloudinary.com/dfa8raimq/image/upload/v1790794178/FTC_Robot_Cyberbyte_fidpxl.jpg"
                  alt="Cyberbyte FTC robot"
                  className="w-full h-32 object-cover"
                  loading="lazy"
                />
              </div>
            </aside>

            {/* Course grid */}
            <div>
              <div className="mb-6">
                <h2 className="font-heading text-2xl font-extrabold text-rsc-navy">Browse Courses</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Click a course to find a session that fits your schedule.
                </p>
              </div>

              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-rsc-blue" />
                </div>
              ) : filteredCourses.length === 0 ? (
                <div className="text-center py-12" data-testid="catalog-empty-state">
                  <Filter className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No courses found</h3>
                  <p className="text-muted-foreground mb-4">Try widening your filters.</p>
                  <Button variant="outline" onClick={clearFilters} data-testid="catalog-empty-clear-filters">Clear Filters</Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
                  {filteredCourses.map((course, idx) => {
                    const cfg          = CATEGORY_CONFIG[normalizeCategory(course.category)] || CATEGORY_CONFIG.technology;
                    const CatIcon      = cfg.icon;
                    const courseSects  = sectionsByCourse[String(course.id)] || [];
                    // Card summary data
                    const hasSessions   = courseSects.length > 0;
                    const earliestDate  = courseSects.map(s => s.start_date).filter(Boolean).sort()[0];
                    const earliestD     = earliestDate ? new Date(earliestDate + 'T00:00') : null;
                    const fromLabel     = earliestD
                      ? earliestD.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : 'Date TBD';
                    const prices        = courseSects.map(s => parseFloat(s.price) || 0).filter(p => p > 0);
                    const minPrice      = prices.length ? Math.min(...prices) : parseFloat(course.base_price) || 0;
                    const priceDisplay  = minPrice > 0 ? `From $${minPrice.toFixed(0)}` : '';
                    const allBands      = hasSessions
                      ? [...new Set(courseSects.flatMap(s => effectiveBands(s, course)))]
                      : (course.grade_bands || []);
                    const sortedBands   = GRADE_ORDER.filter(b => allBands.includes(b));
                    const locationNames = [...new Set(courseSects.map(s => s.location_name).filter(Boolean))];

                    return (
                      <div
                        key={course.id}
                        className="group relative flex flex-col rounded-2xl border border-gray-200/70 bg-white hover:shadow-lg transition-all overflow-hidden"
                        data-testid={`course-card-${idx}`}
                      >
                        {/* Image */}
                        <div className="relative aspect-[16/9] bg-gray-100 overflow-hidden">
                          {course.thumbnail_url ? (
                            <img
                              src={course.thumbnail_url}
                              alt={course.title}
                              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                              loading="lazy"
                            />
                          ) : (
                            <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${cfg.color}`}>
                              <CatIcon className="w-12 h-12 text-white/80" />
                            </div>
                          )}
                          {/* From date */}
                          {hasSessions && (
                            <div className={`absolute top-3 left-3 rounded-lg shadow-md px-2.5 py-1 ${cfg.bg} text-white text-[11px] font-bold`}>
                              {`from ${fromLabel}`}
                            </div>
                          )}
                          {/* Price + session count */}
                          {hasSessions && (
                            <div className="absolute top-3 right-3 rounded-lg bg-white/95 backdrop-blur px-2 py-1 shadow-sm text-right">
                              {priceDisplay && <div className="text-sm font-extrabold text-rsc-navy leading-none">{priceDisplay}</div>}
                              <div className="text-[9px] font-semibold text-gray-500 leading-tight mt-0.5">
                                {courseSects.length} session{courseSects.length !== 1 ? 's' : ''}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Card body */}
                        <div className="p-4 flex flex-col flex-1">
                          {/* Badges */}
                          <div className="flex flex-wrap items-center gap-1.5 mb-2">
                            <Badge className={`text-[10px] px-1.5 py-0 border-0 ${cfg.bg} text-white font-bold inline-flex items-center gap-1`}>
                              <CatIcon className="w-2.5 h-2.5" /> {cfg.label}
                            </Badge>
                            {sortedBands.map(g => (
                              <span key={g} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${GRADE_COLORS[g] || 'bg-gray-100 text-gray-700'}`}>{g}</span>
                            ))}
                          </div>

                          {/* Title */}
                          <h3 className="font-heading font-bold text-[15px] text-rsc-navy leading-snug line-clamp-2 mb-1.5">
                            {course.title}
                          </h3>

                          {/* Locations */}
                          {locationNames.length > 0 && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 mb-3 min-w-0">
                              <MapPin className="w-3 h-3 shrink-0" />
                              <span className="truncate">
                                {locationNames.slice(0, 2).join(' · ')}
                                {locationNames.length > 2 ? ` +${locationNames.length - 2} more` : ''}
                              </span>
                            </div>
                          )}

                          {/* Find a Session / View Course button */}
                          <div className="mt-auto">
                            {hasSessions ? (
                              <Button
                                size="sm"
                                className="w-full rounded-full font-bold text-sm bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright border-0"
                                onClick={() => expandCourse(String(course.id))}
                                data-testid={`course-expand-${idx}`}
                              >
                                <ChevronDown className="w-3.5 h-3.5 mr-1" /> Find a Session
                              </Button>
                            ) : (
                              <Link to={`/courses/${course.id}`}>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full rounded-full font-bold text-sm border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700"
                                  data-testid={`course-details-${idx}`}
                                >
                                  View Course →
                                </Button>
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* ── Session Picker Modal ── */}
      <Dialog open={!!expandedCourseId} onOpenChange={open => { if (!open) setExpandedCourseId(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          {expandedCourseId && (() => {
            const modalCourse = courseById.get(expandedCourseId);
            if (!modalCourse) return null;
            const cfg    = CATEGORY_CONFIG[normalizeCategory(modalCourse.category)] || CATEGORY_CONFIG.technology;
            const CatIcon = cfg.icon;
            return (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold inline-flex items-center gap-1 ${cfg.bg} text-white`}>
                      <CatIcon className="w-2.5 h-2.5" /> {cfg.label}
                    </span>
                  </div>
                  <DialogTitle className="text-rsc-navy font-heading leading-snug">{modalCourse.title}</DialogTitle>
                </DialogHeader>

                <div className="overflow-y-auto flex-1 space-y-4 px-1">
                  {/* Pickers */}
                  <div className="space-y-3">
                    {/* Location */}
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> Location
                      </p>
                      <Select
                        value={picker.locationId || '__none__'}
                        onValueChange={v => v !== '__none__' && handlePickerLocation(v)}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue placeholder="Select location…" />
                        </SelectTrigger>
                        <SelectContent>
                          {pickerData?.availableLocations.map(l => (
                            <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Grade Band */}
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1">
                        <Users className="w-3 h-3" /> Grade Band
                      </p>
                      <Select
                        value={picker.gradeBand || '__none__'}
                        onValueChange={v => v !== '__none__' && handlePickerGradeBand(v)}
                        disabled={!picker.locationId}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue placeholder={picker.locationId ? 'Select grade band…' : 'Select location first'} />
                        </SelectTrigger>
                        <SelectContent>
                          {pickerData?.availableGradeBands.map(b => (
                            <SelectItem key={b} value={b}>{b}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Duration */}
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Duration
                      </p>
                      <Select
                        value={picker.duration || '__none__'}
                        onValueChange={v => v !== '__none__' && setPicker(p => ({ ...p, duration: v }))}
                        disabled={!picker.gradeBand}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue placeholder={picker.gradeBand ? 'Select duration…' : 'Select grade band first'} />
                        </SelectTrigger>
                        <SelectContent>
                          {pickerData?.availableDurations.map(d => (
                            <SelectItem key={d} value={String(d)}>{formatDuration(d)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Matched sessions list */}
                  {pickerData?.matchedSections.length > 0 && (
                    <div className="space-y-2 border-t pt-3">
                      <p className="text-xs font-semibold text-gray-500 mb-2">
                        {pickerData.matchedSections.length} session{pickerData.matchedSections.length !== 1 ? 's' : ''} available
                      </p>
                      {pickerData.matchedSections.map(s => {
                        const startD    = s.start_date ? new Date(s.start_date + 'T00:00') : null;
                        const endD      = s.end_date   ? new Date(s.end_date   + 'T00:00') : null;
                        const fmtDate   = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                        const dateRange = startD && endD
                          ? `${fmtDate(startD)} – ${fmtDate(endD)}`
                          : startD ? fmtDate(startD) : '';
                        const timeStr   = s.start_time && s.end_time ? `${formatTime(s.start_time)} – ${formatTime(s.end_time)}` : '';
                        const fullDays  = Array.isArray(s.meeting_days)
                          ? s.meeting_days.map(d => ({ Mon:'Monday', Tue:'Tuesday', Wed:'Wednesday', Thu:'Thursday', Fri:'Friday', Sat:'Saturday', Sun:'Sunday' })[d] || d).join(' & ')
                          : '';
                        const seatsLeft = Math.max(0, (s.capacity || 0) - (s.enrolled_count || 0));
                        const soldOut   = seatsLeft === 0;
                        const price     = parseFloat(s.price);
                        const seatColor = soldOut ? 'text-rose-600' : seatsLeft <= 3 ? 'text-amber-600' : 'text-emerald-600';
                        return (
                          <div key={s.id} className="rounded-lg bg-gray-50 border p-4 space-y-3">
                            <div className="space-y-1.5">
                              {dateRange && (
                                <div className="flex items-start gap-2 text-sm">
                                  <span className="text-gray-400 mt-0.5">📅</span>
                                  <span className="font-semibold text-rsc-navy">{dateRange}</span>
                                </div>
                              )}
                              {timeStr && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                  {timeStr}
                                </div>
                              )}
                              {fullDays && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <span className="text-gray-400">🗓️</span>
                                  {fullDays}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center justify-between pt-1 border-t border-gray-200">
                              <span className="text-base font-extrabold text-rsc-navy">
                                {price > 0 ? `$${price.toFixed(0)}` : 'Free'}
                              </span>
                              <span className={`text-xs font-semibold ${seatColor}`}>
                                {soldOut ? 'Sold out' : `${seatsLeft} spot${seatsLeft !== 1 ? 's' : ''} left`}
                              </span>
                            </div>
                            <DialogClose asChild>
                              <Link to={`/courses/${modalCourse.id}?section=${s.id}`}>
                                <Button
                                  size="sm"
                                  className={`w-full rounded-full font-bold text-sm ${soldOut ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright'}`}
                                  disabled={soldOut}
                                >
                                  {soldOut ? 'Join Waitlist' : 'View Details →'}
                                </Button>
                              </Link>
                            </DialogClose>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
